
import { useState, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogFooter 
} from '@/components/ui/dialog';
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { graphqlClient } from '@/graphql/client';
import { addPageMutation, AddPageInput } from '@/graphql/mutations/addPage';
import { updatePageMutation, UpdatePageInput } from '@/graphql/mutations/updatePage';
import { 
  Plus, 
  Edit, 
  Trash2, 
  RefreshCw, 
  Image, 
  Building, 
  Globe, 
  Users, 
  Tag 
} from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useAppSelector } from '@/hooks/useAppSelector';
import { fetchCompanies } from '@/utils/companiesHelper';
import { Skeleton } from '../ui/skeleton';

interface Company {
  id?: string;
  slug: string;
  logoUrl: string | null;
  employees: string;
  category: string;
  description: string | null;
  url: string | null;
  legacySlug: string | null;
  pages: CompanyPage[];
}

interface CompanyPage {
  id?: string;
  subslug: string;
  pricingPageUrl: string | null;
  screenshotUrl: string | null;
  screenshotUrlVersion: string | null;
}

export const CompanyManager = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
  const [isCompanyDialogOpen, setIsCompanyDialogOpen] = useState(false);
  const [isPageDialogOpen, setIsPageDialogOpen] = useState(false);
  const [selectedPage, setSelectedPage] = useState<CompanyPage | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [isRefreshing, setIsRefreshing] = useState(false);

  const uniqueCategories = useAppSelector(state => state.supaCompanies.uniqueCategories)
    .filter(cat => cat !== 'All');
  const uniqueEmployees = useAppSelector(state => state.supaCompanies.uniqueEmployees)
    .filter(emp => emp !== 'All');

  const { toast } = useToast();

  // Form state for company dialog
  const [companyForm, setCompanyForm] = useState<Company>({
    slug: '',
    logoUrl: '',
    employees: '',
    category: '',
    description: '',
    url: '',
    legacySlug: null,
    pages: []
  });

  // Form state for page dialog
  const [pageForm, setPageForm] = useState<CompanyPage>({
    subslug: '',
    pricingPageUrl: '',
    screenshotUrl: null,
    screenshotUrlVersion: null
  });

  // Form state for initial pricing page URL when creating a company
  const [initialPricingPageUrl, setInitialPricingPageUrl] = useState('');

  useEffect(() => {
    loadCompanies();
  }, []);

  const loadCompanies = async () => {
    setIsLoading(true);
    try {
      const { data: companiesData } = await supabase.functions.invoke('get-companies');
      
      if (companiesData && companiesData.companies) {
        setCompanies(companiesData.companies);
      }
    } catch (error) {
      console.error('Error loading companies:', error);
      toast({
        title: 'Error',
        description: 'Failed to load companies',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const refreshData = async () => {
    setIsRefreshing(true);
    try {
      await fetchCompanies();
      await loadCompanies();
      toast({
        title: 'Success',
        description: 'Data refreshed successfully',
      });
    } catch (error) {
      console.error('Error refreshing data:', error);
      toast({
        title: 'Error',
        description: 'Failed to refresh data',
        variant: 'destructive',
      });
    } finally {
      setIsRefreshing(false);
    }
  };

  // Filter companies based on search term
  const filteredCompanies = companies.filter(company => 
    company.slug.toLowerCase().includes(searchTerm.toLowerCase()) ||
    company.category.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (company.description && company.description.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  // Open company dialog for creating/editing
  const openCompanyDialog = (company: Company | null = null) => {
    if (company) {
      setCompanyForm({...company});
      setSelectedCompany(company);
      setInitialPricingPageUrl(''); // Reset when editing existing company
    } else {
      setCompanyForm({
        slug: '',
        logoUrl: '',
        employees: '',
        category: '',
        description: '',
        url: '',
        legacySlug: null,
        pages: []
      });
      setSelectedCompany(null);
      setInitialPricingPageUrl(''); // Reset when creating new company
    }
    setIsCompanyDialogOpen(true);
  };

  // Open page dialog for creating/editing
  const openPageDialog = (company: Company, page: CompanyPage | null = null) => {
    setSelectedCompany(company);
    
    if (page) {
      setPageForm({...page});
      setSelectedPage(page);
    } else {
      setPageForm({
        subslug: '',
        pricingPageUrl: '',
        screenshotUrl: null,
        screenshotUrlVersion: null
      });
      setSelectedPage(null);
    }
    
    setIsPageDialogOpen(true);
  };

  // Handle company form submission
  const handleCompanySubmit = async () => {
    try {
      // If creating a new company with initial pricing page URL
      if (!selectedCompany && initialPricingPageUrl) {
        // Create a main page with the provided pricing page URL
        const newCompanyForm = { 
          ...companyForm, 
          pages: [
            {
              subslug: '', // Empty subslug for main page
              pricingPageUrl: initialPricingPageUrl,
              screenshotUrl: null,
              screenshotUrlVersion: null
            }
          ]
        };

        const { data, error } = await supabase.functions.invoke('import-companies', {
          body: { companies: [newCompanyForm] }
        });

        if (error) throw error;
      } else {
        // Regular company save without initial pricing page URL
        const { data, error } = await supabase.functions.invoke('import-companies', {
          body: { companies: [companyForm] }
        });

        if (error) throw error;
      }

      toast({
        title: 'Success',
        description: selectedCompany ? 'Company updated successfully' : 'Company created successfully',
      });

      setIsCompanyDialogOpen(false);
      setInitialPricingPageUrl(''); // Reset after submission
      loadCompanies();
    } catch (error) {
      console.error('Error saving company:', error);
      toast({
        title: 'Error',
        description: 'Failed to save company',
        variant: 'destructive',
      });
    }
  };

  // Handle page form submission
  const handlePageSubmit = async () => {
    if (!selectedCompany) return;
    
    // Ensure company has an ID
    if (!selectedCompany.id) {
      toast({
        title: 'Error',
        description: 'Company must be saved before adding pages',
        variant: 'destructive',
      });
      return;
    }

    try {
      if (selectedPage) {
        // Update existing page using GraphQL mutation
        if (!selectedPage.id) {
          throw new Error('Page ID is required for updates');
        }
        
        const updateInput: UpdatePageInput = {
          subslug: pageForm.subslug || undefined,
          pricing_page_url: pageForm.pricingPageUrl || undefined,
          archived: false, // CompanyManager doesn't handle archived state
        };
        
        await graphqlClient.request(updatePageMutation, {
          id: selectedPage.id,
          input: updateInput,
        });
        
      } else {
        // Add new page using GraphQL mutation
        const addInput: AddPageInput = {
          subslug: pageForm.subslug || undefined,
          pricing_page_url: pageForm.pricingPageUrl || undefined,
          company_id: selectedCompany.id,
        };
        
        await graphqlClient.request(addPageMutation, {
          input: addInput,
        });
      }

      toast({
        title: 'Success',
        description: selectedPage ? 'Page updated successfully' : 'Page created successfully',
      });

      setIsPageDialogOpen(false);
      loadCompanies();
    } catch (error) {
      console.error('Error saving page:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to save page',
        variant: 'destructive',
      });
    }
  };

  return (
    <div className="space-y-6">
      {/* Header with search and refresh controls */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="relative w-full sm:w-72">
          <Input
            placeholder="Search companies..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-8"
          />
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-500"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
        </div>

        <div className="flex items-center gap-2 w-full sm:w-auto">
          <Button
            variant="outline"
            size="sm"
            onClick={refreshData}
            disabled={isRefreshing}
            className="flex gap-1 items-center"
          >
            <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button
            onClick={() => openCompanyDialog()}
            className="flex gap-1 items-center"
          >
            <Plus className="h-4 w-4" />
            Add Company
          </Button>
        </div>
      </div>

      {/* Companies list */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {isLoading ? (
          // Loading skeletons
          Array.from({ length: 6 }).map((_, index) => (
            <Card key={index} className="overflow-hidden">
              <CardHeader className="p-0 h-32 relative">
                <Skeleton className="h-full w-full absolute inset-0" />
              </CardHeader>
              <CardContent className="p-4 space-y-2">
                <Skeleton className="h-4 w-3/4" />
                <Skeleton className="h-4 w-1/2" />
                <Skeleton className="h-4 w-full" />
              </CardContent>
            </Card>
          ))
        ) : filteredCompanies.length > 0 ? (
          filteredCompanies.map((company) => (
            <Card key={company.slug} className="overflow-hidden">
              <CardHeader className="p-0 h-32 relative bg-gray-100 flex items-center justify-center">
                {company.logoUrl ? (
                  <img
                    src={company.logoUrl}
                    alt={`${company.slug} logo`}
                    className="max-h-24 max-w-full object-contain p-2"
                  />
                ) : (
                  <Building className="h-16 w-16 text-gray-400" />
                )}
              </CardHeader>
              <CardContent className="p-4">
                <div className="space-y-2">
                  <div className="flex justify-between items-start">
                    <h3 className="text-lg font-medium truncate">{company.slug}</h3>
                    <div className="flex gap-1">
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        onClick={() => openCompanyDialog(company)}
                        className="h-8 w-8"
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  
                  <div className="flex flex-wrap gap-2 text-xs">
                    {company.category && (
                      <span className="px-2 py-1 bg-purple-100 text-purple-800 rounded-full flex items-center gap-1">
                        <Tag className="h-3 w-3" />
                        {company.category}
                      </span>
                    )}
                    {company.employees && (
                      <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full flex items-center gap-1">
                        <Users className="h-3 w-3" />
                        {company.employees}
                      </span>
                    )}
                  </div>
                  
                  {company.description && (
                    <p className="text-sm text-gray-600 line-clamp-2">{company.description}</p>
                  )}
                </div>
              </CardContent>
              <CardFooter className="p-4 pt-0 flex flex-col">
                <div className="w-full">
                  <h4 className="text-sm font-medium mb-2">Pages ({company.pages.length})</h4>
                  <div className="space-y-2 max-h-32 overflow-y-auto pr-1">
                    {company.pages.map((page, index) => (
                      <div 
                        key={index}
                        className="text-sm p-2 bg-gray-50 rounded flex justify-between items-center"
                      >
                        <span className="truncate flex-1">
                          {page.subslug ? page.subslug : 'main'}
                        </span>
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          onClick={() => openPageDialog(company, page)}
                          className="h-6 w-6 ml-1"
                        >
                          <Edit className="h-3 w-3" />
                        </Button>
                      </div>
                    ))}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    className="mt-3 w-full flex items-center justify-center gap-1"
                    onClick={() => openPageDialog(company)}
                  >
                    <Plus className="h-3 w-3" />
                    Add Page
                  </Button>
                </div>
              </CardFooter>
            </Card>
          ))
        ) : (
          <div className="col-span-full text-center py-10">
            <p className="text-gray-500">No companies found</p>
          </div>
        )}
      </div>

      {/* Company Dialog */}
      <Dialog open={isCompanyDialogOpen} onOpenChange={setIsCompanyDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {selectedCompany ? 'Edit Company' : 'Add New Company'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="slug">Slug <span className="text-red-500">*</span></Label>
              <Input
                id="slug"
                placeholder="e.g. company-name"
                value={companyForm.slug}
                onChange={(e) => setCompanyForm({...companyForm, slug: e.target.value})}
                disabled={!!selectedCompany}
              />
              {!!selectedCompany && (
                <p className="text-xs text-gray-500">Slug cannot be changed after creation</p>
              )}
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="logoUrl">Logo URL</Label>
              <Input
                id="logoUrl"
                placeholder="https://example.com/logo.png"
                value={companyForm.logoUrl || ''}
                onChange={(e) => setCompanyForm({...companyForm, logoUrl: e.target.value})}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="category">Category <span className="text-red-500">*</span></Label>
              <Select
                value={companyForm.category}
                onValueChange={(value) => setCompanyForm({...companyForm, category: value})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {uniqueCategories.map((category) => (
                    <SelectItem key={category} value={category}>
                      {category}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="employees">Employees <span className="text-red-500">*</span></Label>
              <Select
                value={companyForm.employees}
                onValueChange={(value) => setCompanyForm({...companyForm, employees: value})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select company size" />
                </SelectTrigger>
                <SelectContent>
                  {uniqueEmployees.map((size) => (
                    <SelectItem key={size} value={size}>
                      {size}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="url">Website URL</Label>
              <Input
                id="url"
                placeholder="https://example.com"
                value={companyForm.url || ''}
                onChange={(e) => setCompanyForm({...companyForm, url: e.target.value})}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                placeholder="Brief description of the company"
                value={companyForm.description || ''}
                onChange={(e) => setCompanyForm({...companyForm, description: e.target.value})}
                rows={3}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="legacySlug">Legacy Slug</Label>
              <Input
                id="legacySlug"
                placeholder="old-slug (if applicable)"
                value={companyForm.legacySlug || ''}
                onChange={(e) => setCompanyForm({...companyForm, legacySlug: e.target.value})}
              />
              <p className="text-xs text-gray-500">Only needed for backward compatibility</p>
            </div>
            
            {/* Only show pricing page URL field when creating a new company */}
            {!selectedCompany && (
              <div className="space-y-2">
                <Label htmlFor="initialPricingPageUrl">Main Pricing Page URL</Label>
                <Input
                  id="initialPricingPageUrl"
                  placeholder="https://example.com/pricing"
                  value={initialPricingPageUrl}
                  onChange={(e) => setInitialPricingPageUrl(e.target.value)}
                />
                <p className="text-xs text-gray-500">
                  This will create a main page for this company. You can add more pages later.
                </p>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsCompanyDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleCompanySubmit}>
              {selectedCompany ? 'Update Company' : 'Create Company'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Page Dialog */}
      <Dialog open={isPageDialogOpen} onOpenChange={setIsPageDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {selectedPage ? 'Edit Page' : 'Add New Page'}
              {selectedCompany && ` for ${selectedCompany.slug}`}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="subslug">Subslug</Label>
              <Input
                id="subslug"
                placeholder="e.g. enterprise (leave empty for main page)"
                value={pageForm.subslug}
                onChange={(e) => setPageForm({...pageForm, subslug: e.target.value})}
              />
              <p className="text-xs text-gray-500">
                Leave empty for the main pricing page, or add for additional pages like /enterprise
              </p>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="pricingPageUrl">Pricing Page URL <span className="text-red-500">*</span></Label>
              <Input
                id="pricingPageUrl"
                placeholder="https://example.com/pricing"
                value={pageForm.pricingPageUrl || ''}
                onChange={(e) => setPageForm({...pageForm, pricingPageUrl: e.target.value})}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="screenshotUrl">Screenshot URL</Label>
              <Input
                id="screenshotUrl"
                placeholder="https://example.com/screenshot.png"
                value={pageForm.screenshotUrl || ''}
                onChange={(e) => setPageForm({...pageForm, screenshotUrl: e.target.value})}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="screenshotUrlVersion">Screenshot Version</Label>
              <Input
                id="screenshotUrlVersion"
                placeholder="e.g. 20240601"
                value={pageForm.screenshotUrlVersion || ''}
                onChange={(e) => setPageForm({...pageForm, screenshotUrlVersion: e.target.value})}
              />
              <p className="text-xs text-gray-500">
                Format: YYYYMMDD (e.g., 20240601 for June 1, 2024)
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsPageDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handlePageSubmit}>
              {selectedPage ? 'Update Page' : 'Create Page'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
